/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { cookie } from '../lib/cookie.js';
import { getPopupInstances } from '../lib/instances.js';

/*
 * Visit listeners are an extension point: a customer's page registers
 * `Kontextlab.stats.mapListeners` to be told about visits. The Map
 * Viewer never sets it, so it is read off the global rather than imported.
 */
const listeners = () => window.Kontextlab?.stats ?? null;
import { getConfig } from '../lib/config.js';
import { on } from '../lib/events.js';

/*
 * Subscribes through the event bus rather than jQuery.
 *
 * `emit` used to fan out to `jQuery(target).trigger(name)` purely so this
 * module and the paywall lightbox could listen that way. Neither reads the
 * event target, so a plain subscription is equivalent — and it is what let
 * that third channel, and jQuery with it, be removed.
 */

/*
 * Counts this visitor once per billing period, which is what a metered
 * plan is priced on.
 *
 * The backend decides whether a visit is a new one: it is sent the last
 * time this browser was counted and treats anything older than the
 * current period as a new visitor. Remembering that time is therefore
 * the whole of the client's job, and `localStorage` is enough for it —
 * the number it holds is a marker, not an identity, and a browser that
 * refuses storage is simply counted again.
 *
 * It used to be held by evercookie, which mirrored the value into a
 * cache entry, an ETag and the pixels of a PNG so that clearing cookies
 * would not clear it. That is tracking a person across their own attempt
 * to stop being tracked, for a number nobody needs to be that sure of.
 */
function registerVisit() {
    const config = getConfig();
    const url = config?.endpoints?.registerVisit;
    const clientId = config?.client?.id;
    if (!url || !clientId) return;

    const key = `lastVisitedTime-${clientId}`;
    let lastVisit = null;
    try {
        lastVisit = localStorage.getItem(key);
    } catch {
        /* A private window, or storage the visitor has switched off. */
    }

    const body = new FormData();
    if (lastVisit) body.append('timestamp', lastVisit);

    fetch(url, { method: 'POST', body, credentials: 'same-origin' })
        .then((response) => (response.ok ? response.json() : null))
        .then((counted) => {
            if (counted?.status !== 200) return;
            try {
                localStorage.setItem(
                    key,
                    String(Math.floor(Date.now() / 1000))
                );
            } catch {
                /* See above; it means being counted again next time. */
            }
        })
        .catch(() => {
            /* Billing is the server's record, so a failure here is not
               the visitor's problem and must not reach their console. */
        });
}

export function init() {
on('loaded.map', () => {
    registerVisit();
    var cookieKey = 'mapStats';
    var storageKey = 'mapStats';
    var visitsKey = 'v';
    var popupsKey = 'p';
    var mapId = getConfig()?.map?.id || null;
    const { panel: popupPanel, manager: popupManager } =
        getPopupInstances();
    if (!popupPanel && !popupManager) return;

    function notifyMapListeners(mapId, mapStats) {
        if (listeners()?.mapListeners) {
            listeners().mapListeners.forEach((mapCallback) => {
                mapCallback(mapId, mapStats);
            });
        }
    }

    function notifyPopupListeners(mapId, mapStats, popupId, popupStats) {
        if (listeners()?.popupListeners) {
            listeners().popupListeners.forEach((popupCallback) => {
                popupCallback(mapId, mapStats, popupId, popupStats);
            });
        }
    }

    function ensureMapStatsEntry(mapStats, mapId) {
        if (!mapStats[mapId]) {
            mapStats[mapId] = {};
            mapStats[mapId][visitsKey] = 0;
            mapStats[mapId][popupsKey] = {};
        }
    }

    function incrementMapVisits(mapStats, mapId) {
        mapStats[mapId][visitsKey]++;
    }
    function ensurePopupStatsEntry(mapStats, mapId, popupId) {
        if (!mapStats[mapId][popupsKey][popupId]) {
            mapStats[mapId][popupsKey][popupId] = {};
            mapStats[mapId][popupsKey][popupId][visitsKey] = 0;
        }
    }
    function incrementPopupVisits(mapStats, mapId, popupId) {
        mapStats[mapId][popupsKey][popupId][visitsKey]++;
    }

    // Load and remove legacy cookie
    var mapStats = cookie.read(cookieKey); // ES6: let
    cookie.erase(cookieKey);

    // Use local storage because the data is too large for cookies
    mapStats =
        mapStats || JSON.parse(localStorage.getItem(storageKey) || '{}');
    if (mapId == null) return;

    ensureMapStatsEntry(mapStats, mapId);
    incrementMapVisits(mapStats, mapId);
    notifyMapListeners(mapId, mapStats[mapId]);
    localStorage.setItem(storageKey, JSON.stringify(mapStats));

    on('show.popup', () => {
        var popupId =
            popupPanel.sourceId === undefined
                ? popupManager.sourceId
                : popupPanel.sourceId;
        mapStats =
            mapStats ||
            JSON.parse(localStorage.getItem(storageKey) || '{}');
        ensureMapStatsEntry(mapStats, mapId);
        ensurePopupStatsEntry(mapStats, mapId, popupId);
        incrementPopupVisits(mapStats, mapId, popupId);
        notifyPopupListeners(
            mapId,
            mapStats[mapId],
            popupId,
            mapStats[mapId][popupsKey][popupId]
        );
        localStorage.setItem(storageKey, JSON.stringify(mapStats));
    });
});
}
